import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Booking, BookingPatch } from "@/api/bookings";
import { fetchUnits } from "@/api/newBooking";
import { Btn } from "@/components/Btn";
import {
  ADMIN_FIELDS,
  ADMIN_SECTIONS,
  filterFields,
  type AdminFieldDef,
  type AdminSectionId,
} from "./adminFieldSchema";
import { AdminRow } from "./AdminRow";

type Props = {
  bk: Booking;
  onClose: () => void;
  onSave: (patch: BookingPatch) => void;
  saving?: boolean;
  errorMessage?: string | null;
};

export function AdminEditDrawer({ bk, onClose, onSave, saving = false, errorMessage }: Props) {
  const [patch, setPatch] = useState<BookingPatch>({});
  const [section, setSection] = useState<AdminSectionId>("ids");
  const [query, setQuery] = useState("");
  const mainRef = useRef<HTMLDivElement | null>(null);

  const unitsQuery = useQuery({ queryKey: ["units"], queryFn: fetchUnits, staleTime: 5 * 60 * 1000 });

  const visibleFields = useMemo(() => filterFields(query), [query]);

  const dirty = Object.keys(patch).length > 0;
  const touchedBySection = useMemo(() => {
    const map: Record<string, number> = {};
    for (const key of Object.keys(patch)) {
      const def = ADMIN_FIELDS.find((f) => (f.patchKey ?? f.key) === key);
      if (!def) continue;
      map[def.section] = (map[def.section] ?? 0) + 1;
    }
    return map;
  }, [patch]);

  // Cmd/Ctrl+S speichern, Esc schließen (nur wenn nicht dirty)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty && !saving) onSave(patch);
      } else if (e.key === "Escape") {
        if (!dirty) onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dirty, saving, patch, onClose, onSave]);

  // Section-Scroll: beim Wechsel der Sektion an den Anfang scrollen.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [section]);

  const handleChange = (field: AdminFieldDef, next: unknown) => {
    const key = (field.patchKey ?? field.key) as keyof BookingPatch;
    setPatch((prev) => {
      const copy = { ...prev };
      const original = (bk as unknown as Record<string, unknown>)[field.key];
      const same =
        next === original ||
        (next === null && (original === null || original === undefined || original === "")) ||
        (Array.isArray(next) && Array.isArray(original) && JSON.stringify(next) === JSON.stringify(original));
      if (same) {
        delete (copy as Record<string, unknown>)[key as string];
      } else {
        (copy as Record<string, unknown>)[key as string] = next;
      }
      return copy;
    });
  };

  const handleReset = () => setPatch({});

  const tryClose = () => {
    if (dirty) {
      const ok = window.confirm("Es gibt ungespeicherte Änderungen. Wirklich verwerfen?");
      if (!ok) return;
    }
    onClose();
  };

  const visibleInSection = visibleFields.filter((f) => f.section === section);
  const showAllMatches = query.trim().length > 0;
  const fieldsToRender = showAllMatches ? visibleFields : visibleInSection;

  const getValue = (field: AdminFieldDef): unknown => {
    const patchKey = (field.patchKey ?? field.key) as string;
    if (Object.prototype.hasOwnProperty.call(patch, patchKey)) {
      return (patch as unknown as Record<string, unknown>)[patchKey];
    }
    return (bk as unknown as Record<string, unknown>)[field.key];
  };
  const getOriginal = (field: AdminFieldDef): unknown =>
    (bk as unknown as Record<string, unknown>)[field.key];
  const isDirty = (field: AdminFieldDef): boolean => {
    const patchKey = (field.patchKey ?? field.key) as string;
    return Object.prototype.hasOwnProperty.call(patch, patchKey);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.45)",
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) tryClose();
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          width: "100%",
          maxWidth: 1180,
          maxHeight: "calc(100vh - 48px)",
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          boxShadow: "var(--sh-3)",
          overflow: "hidden",
        }}
      >
        {/* Top Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 18px",
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-subtle)",
          }}
        >
          <span className="material-icons-outlined" style={{ color: "var(--accent)", fontSize: 20 }}>
            shield_person
          </span>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg)" }}>Admin · Alle Felder bearbeiten</div>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            {bk.buchungsreferenz || `#${bk.id}`} · {bk.gruppenname || bk.gastname}
          </div>
          <div style={{ flex: 1 }} />
          <input
            type="search"
            placeholder="Felder suchen…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              padding: "5px 10px",
              borderRadius: 6,
              border: "1.5px solid var(--border)",
              background: "var(--bg-card)",
              color: "var(--fg)",
              fontSize: 12,
              fontFamily: "var(--font-body)",
              width: 200,
            }}
          />
          <Btn variant="ghost" size="sm" icon="close" onClick={tryClose}>
            Schließen
          </Btn>
        </div>

        {/* Body: Sidebar + Form */}
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", overflow: "hidden", minHeight: 0 }}>
          <nav
            style={{
              borderRight: "1px solid var(--border)",
              background: "var(--bg-subtle)",
              padding: 8,
              overflowY: "auto",
            }}
          >
            {ADMIN_SECTIONS.map((s) => {
              const active = !showAllMatches && section === s.id;
              const count = touchedBySection[s.id] ?? 0;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setSection(s.id);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 10px",
                    marginBottom: 2,
                    borderRadius: 6,
                    border: "1px solid transparent",
                    background: active ? "var(--bg-card)" : "transparent",
                    color: active ? "var(--brand-900)" : "var(--fg)",
                    fontWeight: active ? 600 : 500,
                    fontSize: 12,
                    fontFamily: "var(--font-body)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ flex: 1 }}>{s.label}</span>
                  {count > 0 && (
                    <span
                      style={{
                        fontSize: 10,
                        background: "var(--brand-700)",
                        color: "#fff",
                        borderRadius: 9,
                        padding: "1px 6px",
                        fontWeight: 600,
                      }}
                      title={`${count} Änderung(en)`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div ref={mainRef} style={{ overflowY: "auto", padding: "12px 20px" }}>
            {!showAllMatches && (
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  background: "var(--bg-card)",
                  paddingBottom: 8,
                  marginBottom: 4,
                  borderBottom: "1px solid var(--border)",
                  zIndex: 1,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg)" }}>
                  {ADMIN_SECTIONS.find((s) => s.id === section)?.label}
                </div>
                {ADMIN_SECTIONS.find((s) => s.id === section)?.hint && (
                  <div style={{ fontSize: 11, color: "var(--fg-subtle)", marginTop: 2 }}>
                    {ADMIN_SECTIONS.find((s) => s.id === section)?.hint}
                  </div>
                )}
              </div>
            )}

            {showAllMatches && (
              <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 8 }}>
                {visibleFields.length} Treffer für „{query}"
              </div>
            )}

            {fieldsToRender.length === 0 ? (
              <div style={{ padding: 24, color: "var(--fg-subtle)", fontSize: 13 }}>
                Keine Felder.
              </div>
            ) : (
              fieldsToRender.map((field) => (
                <AdminRow
                  key={field.key}
                  field={field}
                  value={getValue(field)}
                  previousValue={getOriginal(field)}
                  dirty={isDirty(field)}
                  onChange={(next) => handleChange(field, next)}
                  units={unitsQuery.data}
                />
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 18px",
            borderTop: "1px solid var(--border)",
            background: "var(--bg-subtle)",
          }}
        >
          {errorMessage && (
            <div
              style={{
                fontSize: 12,
                color: "var(--status-cancel)",
                background: "color-mix(in oklab, var(--status-cancel) 12%, transparent)",
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid var(--status-cancel)",
              }}
            >
              {errorMessage}
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            {dirty
              ? `${Object.keys(patch).length} ungespeicherte Änderung(en)`
              : "Keine Änderungen"}
          </div>
          <div style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" onClick={handleReset} disabled={!dirty || saving}>
            Zurücksetzen
          </Btn>
          <Btn variant="secondary" size="sm" onClick={tryClose} disabled={saving}>
            {dirty ? "Verwerfen" : "Schließen"}
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            icon="save"
            onClick={() => onSave(patch)}
            disabled={!dirty || saving}
          >
            Übernehmen
          </Btn>
        </div>
      </div>
    </div>
  );
}
